import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Bell } from 'lucide-react'
import * as Popover from '@radix-ui/react-popover'

import { api } from '@/lib/api'
import { useWorkspaceStore } from '@/stores/workspace.store'
import type { ApiResponse } from '@/types/api'
import type { NotificationFull } from '@/types/models'
import NotificationPanel from './NotificationPanel'

/**
 * NotificationBell — sidebar bell icon with unread count badge.
 *
 * Polls GET /api/v1/notifications?unread=true every 30 seconds.
 * Clicking opens NotificationPanel in a Radix Popover.
 */
export default function NotificationBell() {
  const [open, setOpen] = useState(false)
  const workspaceId = useWorkspaceStore((s) => s.currentWorkspaceId)

  const { data } = useQuery<ApiResponse<NotificationFull[]>>({
    queryKey: ['notifications-unread', workspaceId],
    queryFn: () => api.get<ApiResponse<NotificationFull[]>>('/v1/notifications?unread=true'),
    staleTime: 30_000,
    refetchInterval: 30_000,
    enabled: !!workspaceId,
  })

  const unreadCount = data?.data?.length ?? 0

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button
          type="button"
          className="relative flex items-center justify-center rounded-md p-2 text-muted-foreground hover:bg-accent hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-label={`Notifications${unreadCount > 0 ? `, ${unreadCount} unread` : ''}`}
        >
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <span className="absolute right-1 top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-0.5 text-[10px] font-bold text-white">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          side="right"
          align="end"
          sideOffset={8}
          className="z-50 w-80 rounded-xl border bg-popover shadow-xl outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"
        >
          <NotificationPanel onClose={() => setOpen(false)} />
          <Popover.Arrow className="fill-border" />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  )
}
